import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Collapsible, CollapsibleTrigger, CollapsibleContent,
} from "@/components/ui/collapsible";
import { MessageCircle, Mail, Lock, ChevronDown, IndianRupee } from "lucide-react";

const DEFAULT_WA_BODY = `Hi {{first_name}},

This is {{sender_name}} from RENVIRO Systems. {{company_name}} was on our list for a quick note —

[type your outreach message here]

Happy to share more details if this is relevant.

— {{sender_name}}`;

const PRIORITIES = [
  { v: "low",      l: "Low" },
  { v: "medium",   l: "Medium" },
  { v: "high",     l: "High" },
  { v: "critical", l: "Critical" },
];

/**
 * CampaignBuilder — Pick segment → compose message → spawn a campaign.
 * Channel is effectively WhatsApp-only for now (Email locked pending SMTP).
 */
export default function CampaignBuilder({ open, onClose, onCreated }) {
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [form, setForm] = useState({
    name: "",
    channel: "whatsapp",
    segment_id: "",
    template_id: "",
    subject: "",
    body: DEFAULT_WA_BODY,
    // Phase-1 planning fields
    campaign_type: "whatsapp",
    description: "",
    target_industry: "",
    region: "",
    priority: "medium",
    budget: "",
    expected_leads: "",
    expected_quotations: "",
    expected_revenue: "",
    expected_orders: "",
    start_date: "",
    end_date: "",
  });
  const [planOpen, setPlanOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { total, sample }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: "", channel: "whatsapp", segment_id: "", template_id: "",
      subject: "", body: DEFAULT_WA_BODY,
      campaign_type: "whatsapp", description: "", target_industry: "", region: "",
      priority: "medium", budget: "", expected_leads: "", expected_quotations: "",
      expected_revenue: "", expected_orders: "", start_date: "", end_date: "",
    });
    setPreview(null);
    setPlanOpen(false);
    Promise.all([
      api.get("/segments").then(({ data }) => setSegments(data || [])),
      api.get("/templates?channel=whatsapp").then(({ data }) => setTemplates(data || [])).catch(() => setTemplates([])),
      api.get("/marketing/campaign-meta").then(({ data }) => setCampaignTypes(data?.campaign_types || [])).catch(() => setCampaignTypes([])),
    ]);
  }, [open]);

  useEffect(() => {
    if (!form.segment_id) { setPreview(null); return; }
    api.get(`/segments/${form.segment_id}/preview?limit=5`).then(({ data }) => {
      setPreview({ total: data.total || 0, sample: data.rows || [] });
    }).catch(() => setPreview({ total: 0, sample: [] }));
  }, [form.segment_id]);

  const applyTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (tpl) {
      setForm((f) => ({ ...f, template_id: tplId, subject: tpl.subject || f.subject, body: tpl.body || f.body }));
    } else {
      setForm((f) => ({ ...f, template_id: "" }));
    }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Give the campaign a name"); return; }
    if (!form.segment_id) { toast.error("Pick an audience segment"); return; }
    if (!form.body.trim()) { toast.error("Message body can't be empty"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/campaigns", {
        name: form.name.trim(),
        channel: form.channel,
        segment_id: form.segment_id,
        template_id: form.template_id || null,
        subject: form.channel === "email" ? (form.subject || null) : null,
        body: form.body,
        // Phase-1 planning fields. Sent on every create — backend treats
        // them as optional so the existing flow keeps working when the
        // builder leaves them blank.
        campaign_type: form.campaign_type || form.channel,
        description: form.description || null,
        target_industry: form.target_industry || null,
        region: form.region || null,
        priority: form.priority || "medium",
        budget: Number(form.budget) || 0,
        expected_leads: Number(form.expected_leads) || 0,
        expected_quotations: Number(form.expected_quotations) || 0,
        expected_revenue: Number(form.expected_revenue) || 0,
        expected_orders: Number(form.expected_orders) || 0,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      });
      toast.success(`Campaign queued · ${data.total_count} recipients`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[680px] rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New WhatsApp campaign</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            You'll manually send each message one at a time — no bulk blast, no spam flags. Email is coming soon.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Campaign name</Label>
            <Input data-testid="camp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cement plants · Feb warm-up" />
          </div>

          {/* Channel picker */}
          <div className="space-y-1.5">
            <Label>Channel</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="camp-channel-whatsapp"
                onClick={() => setForm({ ...form, channel: "whatsapp" })}
                className={`p-3 rounded-2xl border-2 text-left tap transition ${form.channel === "whatsapp" ? "border-primary bg-primary-50" : "border-border/60 hover:bg-muted/50"}`}
              >
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm">WhatsApp</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Manual send, one tab at a time.</p>
              </button>
              <div
                data-testid="camp-channel-email-disabled"
                className="p-3 rounded-2xl border-2 border-dashed border-border/60 bg-muted/30 text-left opacity-70 cursor-not-allowed"
              >
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-sm">Email</span>
                  <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Coming soon — SMTP setup pending.</p>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Audience segment</Label>
            <Select value={form.segment_id} onValueChange={(v) => setForm({ ...form, segment_id: v })}>
              <SelectTrigger data-testid="camp-segment"><SelectValue placeholder={segments.length ? "Pick a segment" : "No segments yet — create one first"} /></SelectTrigger>
              <SelectContent>
                {segments.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} · {s.match_count ?? 0} contacts
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {preview && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="camp-audience-preview">
                {preview.total} contact{preview.total === 1 ? "" : "s"} in this audience
                {preview.sample.length > 0 ? ` — e.g. ${preview.sample.slice(0, 3).map((r) => r.company_name || r.name).join(", ")}${preview.total > 3 ? "…" : ""}` : ""}
              </p>
            )}
          </div>

          {templates.length > 0 && (
            <div className="space-y-1.5">
              <Label>Start from template (optional)</Label>
              <Select value={form.template_id || "__none"} onValueChange={(v) => applyTemplate(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="camp-template"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None — write from scratch</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} · {t.category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Plan & Budget — collapsible so the basic campaign flow stays
              one-step. Surfaces all the Phase-1 Growth Engine fields the
              founder asked for (budget, ROI targets, dates, industry). */}
          <Collapsible open={planOpen} onOpenChange={setPlanOpen} className="rounded-2xl border border-border/60 bg-muted/20">
            <CollapsibleTrigger className="w-full flex items-center justify-between p-3 tap" data-testid="camp-plan-toggle">
              <div className="flex items-center gap-2">
                <IndianRupee className="h-4 w-4 text-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Plan, budget & ROI targets</span>
              </div>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition ${planOpen ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="p-4 pt-1 space-y-3 border-t border-border/60">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Campaign type</Label>
                  <Select value={form.campaign_type} onValueChange={(v) => setForm({ ...form, campaign_type: v })}>
                    <SelectTrigger data-testid="camp-type" className="rounded-2xl h-11"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="rounded-2xl max-h-72">
                      {campaignTypes.map((t) => (
                        <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="camp-priority" className="rounded-2xl h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      {PRIORITIES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Target industry</Label>
                  <Input className="rounded-2xl h-11" value={form.target_industry} onChange={(e) => setForm({ ...form, target_industry: e.target.value })} placeholder="e.g. Cement, Steel, Power" data-testid="camp-industry" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Region</Label>
                  <Input className="rounded-2xl h-11" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="South India, Pune, …" data-testid="camp-region" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Start date</Label>
                  <Input type="date" className="rounded-2xl h-11" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="camp-start-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">End date</Label>
                  <Input type="date" className="rounded-2xl h-11" value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="camp-end-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Budget (₹)</Label>
                  <Input type="number" className="rounded-2xl h-11" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="50000" data-testid="camp-budget" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Expected revenue (₹)</Label>
                  <Input type="number" className="rounded-2xl h-11" value={form.expected_revenue} onChange={(e) => setForm({ ...form, expected_revenue: e.target.value })} placeholder="500000" data-testid="camp-expected-revenue" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Expected leads</Label>
                  <Input type="number" className="rounded-2xl h-11" value={form.expected_leads} onChange={(e) => setForm({ ...form, expected_leads: e.target.value })} placeholder="100" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Expected quotations</Label>
                  <Input type="number" className="rounded-2xl h-11" value={form.expected_quotations} onChange={(e) => setForm({ ...form, expected_quotations: e.target.value })} placeholder="20" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Expected orders</Label>
                  <Input type="number" className="rounded-2xl h-11" value={form.expected_orders} onChange={(e) => setForm({ ...form, expected_orders: e.target.value })} placeholder="5" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-semibold">Description / notes</Label>
                  <Textarea rows={2} className="rounded-2xl" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's this campaign trying to achieve?" />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="space-y-1.5">
            <Label>Message body</Label>
            <Textarea rows={8} data-testid="camp-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="font-mono text-sm" />
            <p className="text-[11px] text-muted-foreground">
              Placeholders: <code>{`{{first_name}}`}</code> · <code>{`{{company_name}}`}</code> · <code>{`{{sender_name}}`}</code> · <code>{`{{phone}}`}</code>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={saving} data-testid="camp-create">
            {saving ? "Queuing…" : "Queue campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
